import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// Tema de la app: claro / oscuro / seguir al sistema. La preferencia se guarda
// en localStorage (clave `theme`) y se aplica agregando/quitando la clase `dark`
// en <html> (Tailwind darkMode: 'class'). El parpadeo inicial lo evita el script
// anti-FOUC de index.html, que usa la MISMA clave y lógica.

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'theme';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

function resolve(theme: Theme): ResolvedTheme {
  if (theme === 'system') return prefersDark() ? 'dark' : 'light';
  return theme;
}

function readStored(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* localStorage no disponible (SSR / navegador restringido) */
  }
  return 'system';
}

function apply(resolved: ResolvedTheme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
  }
}

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(() => readStored());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolve(theme));

  // Re-resolver y aplicar cada vez que cambia la preferencia.
  useEffect(() => {
    const r = resolve(theme);
    setResolvedTheme(r);
    apply(r);
  }, [theme]);

  // Cuando seguimos al sistema, reaccionar a cambios de prefers-color-scheme.
  useEffect(() => {
    if (theme !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => {
      const r: ResolvedTheme = mql.matches ? 'dark' : 'light';
      setResolvedTheme(r);
      apply(r);
    };
    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, [theme]);

  const setTheme = (next: Theme): void => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage no disponible */
    }
    setThemeState(next);
  };

  return <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de <ThemeProvider>');
  return ctx;
}
